/**
 * A draggable column edge.
 *
 * The rail and the assistant were fixed widths in the first build, which forced
 * every photographer into one idea of how much room the controls deserve. This
 * is a hit area rather than a visible bar: the line only shows under the
 * pointer, so the chrome stays quiet until someone reaches for it.
 */

import {
  useEffect,
  useRef,
  useState,
  type KeyboardEvent as ReactKeyboardEvent,
  type PointerEvent as ReactPointerEvent,
} from 'react';

/** Arrow-key nudge, matched to the smallest width change worth seeing. */
const STEP = 16;

interface ResizerProps {
  /** Which side of the window the column sits on. Decides which way a drag grows it. */
  side: 'left' | 'right';
  width: number;
  min: number;
  max: number;
  onChange: (width: number) => void;
  /** Names the column being sized, for assistive technology. */
  label: string;
  /** Width restored on a double click. */
  defaultWidth: number;
}

export function Resizer({ side, width, min, max, onChange, label, defaultWidth }: ResizerProps) {
  // Two records of the same fact, deliberately. The ref is what the pointer
  // handlers read, because a quick drag delivers its first `pointermove` before
  // React has committed the state update from `pointerdown` — gating the move on
  // state meant a fast flick was silently ignored. The state exists only to put
  // the `dragging` class on the element.
  const draggingRef = useRef(false);
  const [dragging, setDragging] = useState(false);
  const origin = useRef({ x: 0, width });

  useEffect(() => {
    if (!dragging) return;
    // A drag across a panel full of labels would otherwise paint a text
    // selection behind the pointer, and the cursor would flicker between the
    // handle and whatever it passes over.
    const { style } = document.body;
    const priorSelect = style.userSelect;
    const priorCursor = style.cursor;
    style.userSelect = 'none';
    style.cursor = 'col-resize';
    return () => {
      style.userSelect = priorSelect;
      style.cursor = priorCursor;
    };
  }, [dragging]);

  const clamp = (value: number) => Math.min(max, Math.max(min, Math.round(value)));

  const handlePointerDown = (event: ReactPointerEvent<HTMLDivElement>) => {
    if (event.button !== 0) return;
    event.preventDefault();
    event.currentTarget.setPointerCapture(event.pointerId);
    origin.current = { x: event.clientX, width };
    draggingRef.current = true;
    setDragging(true);
  };

  const handlePointerMove = (event: ReactPointerEvent<HTMLDivElement>) => {
    if (!draggingRef.current) return;
    const travel = event.clientX - origin.current.x;
    onChange(clamp(origin.current.width + (side === 'left' ? travel : -travel)));
  };

  const stop = (event: ReactPointerEvent<HTMLDivElement>) => {
    if (!draggingRef.current) return;
    if (event.currentTarget.hasPointerCapture(event.pointerId)) {
      event.currentTarget.releasePointerCapture(event.pointerId);
    }
    draggingRef.current = false;
    setDragging(false);
  };

  const handleKeyDown = (event: ReactKeyboardEvent<HTMLDivElement>) => {
    // The handle moves the way the key points, so the column on the right grows
    // when the divider walks left.
    const toward = event.key === 'ArrowLeft' ? -1 : event.key === 'ArrowRight' ? 1 : 0;
    if (toward === 0) return;
    event.preventDefault();
    onChange(clamp(width + STEP * toward * (side === 'left' ? 1 : -1)));
  };

  return (
    <div
      role="separator"
      aria-orientation="vertical"
      aria-label={label}
      aria-valuenow={width}
      aria-valuemin={min}
      aria-valuemax={max}
      aria-valuetext={`${width} pixels`}
      tabIndex={0}
      className={`resizer${dragging ? ' dragging' : ''}`}
      style={side === 'left' ? { right: -4 } : { left: -4 }}
      onPointerDown={handlePointerDown}
      onPointerMove={handlePointerMove}
      onPointerUp={stop}
      onPointerCancel={stop}
      onDoubleClick={() => onChange(clamp(defaultWidth))}
      onKeyDown={handleKeyDown}
    />
  );
}
